import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";

export default function ServiceDetail() {
    const { slug } = useParams();
    const [service, setService] = useState(null);
    const [err, setErr] = useState("");

    useEffect(() => {
        api.get(`/services/${slug}`)
            .then((r) => setService(r.data))
            .catch(() => setErr("Service not found"));
    }, [slug]);

    if (err) {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center">
                <p className="text-white/70">{err}</p>
                <Link to="/services" className="mt-6 text-gold-500">← Back to services</Link>
            </div>
        );
    }
    if (!service) return <div className="min-h-[60vh] flex items-center justify-center text-gold-500">Loading…</div>;

    return (
        <div data-testid="service-detail-page">
            <section className="mx-auto max-w-5xl px-6 md:px-12 pt-20 pb-6">
                <Link to="/services" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-gold-500">
                    <ArrowLeft size={16} /> All services
                </Link>
            </section>
            <section className="mx-auto max-w-5xl px-6 md:px-12 pb-16">
                <div className="label-tag">Service</div>
                <h1 className="mt-4 font-display text-5xl md:text-6xl font-black tracking-tight leading-[1.05]">
                    {service.title}
                </h1>
                <p className="mt-6 text-2xl font-display italic text-gold-500">
                    {service.tagline}
                </p>
                <p className="mt-8 text-white/70 text-lg leading-relaxed max-w-3xl">
                    {service.summary}
                </p>

                {service.external_url && (
                    <a
                        href={service.external_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="service-external-cta"
                        className="mt-8 inline-flex items-center gap-3 rounded-2xl border border-gold-500/40 bg-gold-500/5 px-6 py-5 hover:border-gold-500 hover:bg-gold-500/10 transition-all group max-w-2xl"
                    >
                        <div className="flex-1">
                            <div className="label-tag">Free companion resource</div>
                            <div className="mt-1 font-display text-lg font-semibold text-white">
                                {service.external_label}
                            </div>
                        </div>
                        <ExternalLink size={20} className="text-gold-500 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                    </a>
                )}
            </section>

            <section className="bg-ink-800 border-y border-white/10 py-20">
                <div className="mx-auto max-w-5xl px-6 md:px-12 grid md:grid-cols-2 gap-10">
                    <div>
                        <div className="label-tag">What you get</div>
                        <ul className="mt-6 space-y-4">
                            {service.deliverables.map((d) => (
                                <li key={d} className="flex gap-3 text-white/85">
                                    <CheckCircle2 size={20} className="text-gold-500 shrink-0 mt-0.5" />
                                    <span>{d}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="rounded-2xl bg-ink-900 border border-gold-500/20 p-8">
                        <div className="label-tag">How we start</div>
                        <ol className="mt-6 space-y-5 text-white/80">
                            {["Free 30-min scoping call",
                              "Written plan &amp; flat-fee estimate",
                              "Kickoff within 7 business days",
                              "Weekly progress reports"].map((step, i) => (
                                <li key={i} className="flex gap-4">
                                    <span className="font-display text-gold-500 font-bold w-8 shrink-0">0{i + 1}</span>
                                    <span dangerouslySetInnerHTML={{ __html: step }} />
                                </li>
                            ))}
                        </ol>
                        <Link
                            to="/booking"
                            state={{ service: service.slug }}
                            data-testid="service-book-cta"
                            className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gold-500 text-ink-900 font-bold uppercase tracking-wider text-sm hover:bg-gold-400"
                        >
                            Book Consultation
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
